import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplicationState } from 'store';
import { ConlicitasegState, QuoteFlow } from 'store/modules/conlicitaseg_store/types';
import {
  clearFormQuote,
  consumeFidelizeLoginRedirect,
  getFidelizeBiddingGuarantee,
  markFidelizeEmailLinkApplied,
  setOriginChannel,
  setQuoteFlow,
} from 'store/modules/conlicitaseg_store/actions';
import { ProfileState } from 'store/modules/profile_store/types';
import { useQuery } from 'services/query_services';
import { FIDELIZE_EMAIL_ORIGIN, getFidelizeBidding, QUOTE_INTRO_PATH } from 'services/conlicitaseg';
import { getDefaultQuoteFlow, getFlowEntryPath } from 'services/conlicitaseg_flow';
import createTrack from 'services/actions/tracker_actions';
import useCanUploadContract from 'hooks/useCanUploadContract';
import useOnlyConlicitaseg from 'hooks/useOnlyConlicitaseg';
import useQuoteFlow from 'hooks/useQuoteFlow';
import Loader from 'components/atoms/loader';

const BiddingQuoteIntro: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const {
    guarantee_type,
    quote_flow,
    fidelize_login_redirect_pending,
    fidelize_email_link_applied,
    request,
    current_process_id,
  } = useSelector<ApplicationState, ConlicitasegState>((state) => state.session.conlicitaseg);
  const location = useLocation<{ prefilled?: boolean } | undefined>();
  const isLoadingFidelizeData = !!request?.get_fidelize_bidding_guarantee?.loading;
  const isOnlyConlicitaseg = useOnlyConlicitaseg();
  const canUploadContract = useCanUploadContract();
  const { config, pathFor } = useQuoteFlow();
  const isBiddingIntro = config.introVariant === 'bidding';

  useEffect(() => {
    const keepPrefill = !!location.state?.prefilled && !current_process_id;
    let flow: QuoteFlow | null = keepPrefill ? quote_flow : null;
    if (!keepPrefill) {
      dispatch(clearFormQuote());
    }
    if (!flow || flow === 'bidding') {
      flow = getDefaultQuoteFlow(isOnlyConlicitaseg);
      dispatch(setQuoteFlow(flow));
    }
    if (query.get('origem') === 'whatsapp') {
      dispatch(setOriginChannel('whatsapp'));
    }
    if (query.get('origem') === 'fidelize') {
      const fidelizeBidding = fidelize_login_redirect_pending
        ? getFidelizeBidding(profile, isOnlyConlicitaseg)
        : null;

      if (fidelizeBidding) {
        dispatch(clearFormQuote());
        flow = 'fidelize';
        dispatch(setQuoteFlow('fidelize', 'execucao'));
        dispatch(setOriginChannel('fidelize'));
        dispatch(getFidelizeBiddingGuarantee(fidelizeBidding, profile));
        dispatch(consumeFidelizeLoginRedirect());
        createTrack('ConlicitaSeg - Redirecionado para cotação', { fonte: 'Login Fidelize' });
      }

      history.replace(QUOTE_INTRO_PATH);
    }
    if (query.get('origem') === FIDELIZE_EMAIL_ORIGIN) {
      const fidelizeBidding = !fidelize_email_link_applied
        ? getFidelizeBidding(profile, isOnlyConlicitaseg)
        : null;

      if (fidelizeBidding) {
        dispatch(clearFormQuote());
        flow = 'fidelize';
        dispatch(setQuoteFlow('fidelize', 'execucao'));
        dispatch(setOriginChannel('fidelize'));
        dispatch(getFidelizeBiddingGuarantee(fidelizeBidding, profile));
        dispatch(consumeFidelizeLoginRedirect());
        dispatch(markFidelizeEmailLinkApplied());
        createTrack('ConlicitaSeg - Redirecionado para cotação', {
          fonte: 'Link e-mail Fidelize',
        });
      }

      history.replace(QUOTE_INTRO_PATH);
    }

    const entryPath = flow ? getFlowEntryPath(flow, canUploadContract) : null;
    if (entryPath) history.replace(entryPath);
  }, []);

  const handleCancel = () => {
    dispatch(clearFormQuote());
    history.push('/conlicitaseg');
  };

  const handleContinue = () => {
    history.push(pathFor(guarantee_type === 'participacao' ? 'tomador' : 'motivo'));
  };

  if (config.introVariant === 'none') {
    return (
      <div className="page-container">
        <div className="container-fluid big-resolutions py-5 px-md-5">
          <div className="row justify-content-center mt-5">
            <div className="col-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions py-5 px-md-5">
        <div className="row justify-content-center mt-5">
          <div className="col-12 mx-auto">
            <button type="button" className="mb-3 px-0 btn btn-link" onClick={handleCancel}>
              <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar para lista
              de cotações e apólices
            </button>
            <div className="shadow-sm p-4 bg-white rounded">
              <h3 className="mb-3">Você está a poucos cliques do seu Seguro Garantia!</h3>
              {isBiddingIntro && (
                <p>
                  A seguir iremos iniciar o processo de cotação do seu Seguro Garantia, confira os
                  dados com atenção.
                </p>
              )}
              {!isBiddingIntro && (
                <p>
                  Para começarmos, tenha em mãos{' '}
                  <strong>um documento que comprove a solicitação da garantia</strong> como o
                  edital, termo de referência, e-mail do órgão ou contrato da licitação.
                </p>
              )}
              <p>
                Em caso de dúvidas,{' '}
                <a href="/conlicitaseg/contato" target="_blank" rel="noopener noreferrer">
                  fale com um de nossos especialistas
                </a>
                .
              </p>
              {!isBiddingIntro && (
                <>
                  <p>Quando estiver pronto, passe para o próximo passo.</p>
                  <p className="mb-0">
                    <strong>
                      Solicitações de endosso, como prorrogações, renovações, inclusão de cobertura
                      ou outras alterações, devem ser tratadas diretamente com nossos especialistas.
                    </strong>
                  </p>
                  <p>
                    <strong>
                      <a href="/conlicitaseg/contato" target="_blank" rel="noopener noreferrer">
                        Entre em contato
                      </a>{' '}
                      para receber o suporte adequado.
                    </strong>
                  </p>
                </>
              )}
              <div className="alert alert-warning mt-4" role="alert">
                <div className="d-flex align-items-center mb-2">
                  <i className="material-icons mr-2" style={{ fontSize: '20px' }}>
                    warning
                  </i>
                  <strong>Confirmação de Vínculo Legal</strong>
                </div>
                <p className="mb-0">
                  Ao prosseguir, declaro que estou legalmente apto para gerar cotação e eventual
                  emissão de apólice de seguro garantia. Estou ciente que uma vez emitida a apólice
                  não há possibilidade de cancelamento ficando a{' '}
                  <strong>{profile.company_data?.data?.razao_social}</strong> responsável pelas
                  obrigações por ela geradas.
                </p>
              </div>
              <div className="d-flex justify-content-end mt-4">
                <button type="button" className="btn btn-outline-secondary" onClick={handleCancel}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary ml-2"
                  onClick={handleContinue}
                  disabled={isLoadingFidelizeData}
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiddingQuoteIntro;
